import { Card, CardActions, CardContent, Grid, Typography, useEventCallback } from '@mui/material';
import { PmiButton } from 'shell/components/shared/button';
import { useFormik } from 'formik';
import { ReactElement, useCallback } from 'react';
import * as yup from 'yup';
import AddressAutoComplete from '../../shared/search-address';
import PmiTextField from 'shell/components/shared/text-field';
import StreetNumberList, { StreetNumberVM } from 'system/components/shared/street-number-list';
import { CirculationAreaClient } from 'shell/view-models/circulation-area-client';

export interface TownHallForm {
  code: string;
  shortDescription: string;
  streetNumber: string;
  description: string;
  address: CirculationAreaClient | null;
  civicRepresentative: string;
}

const validationSchema = yup.object({
  code: yup.string().nullable().required('La zona è obbligatorio'),
  shortDescription: yup.string().nullable().required('La descrizione è obbligatorio'),
  description: yup.string().nullable().required('La descrizione è obbligatorio'),
  streetNumber: yup
    .number()
    .nullable()
    .required('Il numero civico è obbligatorio')
    .typeError('Il numero civico deve essere un numero'),
  address: yup
    .object()
    .nullable()
    .shape({
      code: yup.number().required("L'indirizzo è obbligatorio"),
    }),
});

interface FormTownHallProps {
  onOk: (form?: TownHallForm) => void;
  onCancel: (form?: TownHallForm) => void;
  title: string;
  children?: ReactElement;
  disabled?: boolean;
  initialValues?: TownHallForm;
  isEdit?: boolean;
}

const FormTownHall = ({
  onOk,
  onCancel,
  title,
  children,
  disabled = false,
  initialValues,
  isEdit,
}: FormTownHallProps): React.ReactElement => {
  const onSubmit = useCallback(
    (formValues) => {
      onOk(formValues);
    },
    [onOk]
  );

  const townHallForm = useFormik({
    initialValues: initialValues || {
      code: '',
      shortDescription: '',
      description: '',
      streetNumber: '',
      address: null,
      civicRepresentative: '',
    },
    validationSchema: validationSchema,
    onSubmit,
    enableReinitialize: true,
  });

  const onChangeStreetNumberList = useEventCallback((item: StreetNumberVM | null) => {
    townHallForm.setFieldValue('streetNumber', item?.id || '');
  });

  return (
    <Card raised className="pmi-my-3">
      <form onSubmit={townHallForm.handleSubmit}>
        <CardContent style={{ padding: '24px' }}>
          <Typography gutterBottom variant="h5" component="h2">
            {title}
          </Typography>
          <div style={{ padding: '24px 0 0 0' }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={2}>
                {/* <Tooltip arrow title="Il campo è generato automaticamente">
                  <div> */}
                <PmiTextField
                  label="Zona *"
                  id="code"
                  name="code"
                  fullWidth
                  type="text"
                  variant="outlined"
                  value={townHallForm.values.code || ''}
                  inputProps={{
                    maxLength: '2',
                  }}
                  disabled={disabled || isEdit}
                  onChange={townHallForm.handleChange}
                  error={townHallForm.touched.code && Boolean(townHallForm.errors.code)}
                  helperText={townHallForm.errors.code}
                />
                {/* </div>
                </Tooltip> */}
              </Grid>
              <Grid item xs={12} md={10}>
                <PmiTextField
                  label="Descrizione breve *"
                  id="shortDescription"
                  name="shortDescription"
                  fullWidth
                  type="text"
                  variant="outlined"
                  value={townHallForm.values.shortDescription || ''}
                  disabled={disabled}
                  inputProps={{
                    maxLength: '20',
                  }}
                  onChange={townHallForm.handleChange}
                  error={
                    townHallForm.touched.shortDescription &&
                    Boolean(townHallForm.errors.shortDescription)
                  }
                  helperText={townHallForm.errors.shortDescription}
                />
              </Grid>
              <Grid item xs={12} md={12}>
                <PmiTextField
                  label="Descrizione *"
                  id="description"
                  name="description"
                  fullWidth
                  type="text"
                  variant="outlined"
                  inputProps={{
                    maxLength: '50',
                  }}
                  disabled={disabled}
                  value={townHallForm.values.description || ''}
                  onChange={townHallForm.handleChange}
                  error={
                    townHallForm.touched.description && Boolean(townHallForm.errors.description)
                  }
                  helperText={townHallForm.errors.description}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <AddressAutoComplete
                  required
                  error={townHallForm.touched.address && Boolean(townHallForm.errors.address)}
                  selectedValue={townHallForm.values.address || null}
                  handleChange={(value) => {
                    townHallForm.setFieldValue('address', value);
                  }}
                  disabled={disabled}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <StreetNumberList
                  label="Numero civico *"
                  onChange={onChangeStreetNumberList}
                  valueStreetNumber={
                    townHallForm.values.streetNumber
                      ? {
                          id: townHallForm.values.streetNumber,
                          value: townHallForm.values.streetNumber.toString(),
                        }
                      : null
                  }
                  disabled={disabled}
                  circulationArea={townHallForm.values.address?.code?.toString() || ''}
                  error={
                    townHallForm.touched.streetNumber && Boolean(townHallForm.errors.streetNumber)
                  }
                  helperText={townHallForm.errors.streetNumber}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <PmiTextField
                  label="Esponente civico"
                  id="civicRepresentative"
                  name="civicRepresentative"
                  fullWidth
                  variant="outlined"
                  value={townHallForm.values.civicRepresentative || ''}
                  onChange={townHallForm.handleChange}
                  inputProps={{
                    maxLength: '3',
                  }}
                  disabled={disabled}
                  error={
                    townHallForm.touched.civicRepresentative &&
                    Boolean(townHallForm.errors.civicRepresentative)
                  }
                  helperText={townHallForm.errors.civicRepresentative}
                />
              </Grid>
            </Grid>
          </div>
        </CardContent>
        {children}
        <CardActions style={{ padding: '24px 24px 16px 24px' }}>
          <PmiButton variant="contained" color="primary" type="submit" disabled={disabled}>
            Conferma
          </PmiButton>
          <PmiButton
            variant="outlined"
            color="cdmBrand"
            onClick={() => {
              onCancel(townHallForm.values);
            }}
          >
            Indietro
          </PmiButton>
        </CardActions>
      </form>
    </Card>
  );
};

FormTownHall.displayName = 'Form Town Hall component';

export default FormTownHall;
